import { DEFAULT_API_BASE_URL } from '../core/config';

export type StorefrontContactTicketInput = {
  shopSlug: string;
  email: string;
  message: string;
  title?: string;
  name?: string;
  invoiceId?: string;
  apiBaseUrl?: string;
};

export type StorefrontContactTicketResult = {
  uniqid: string;
};

function isLocalDevHost(host: string): boolean {
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost')) {
    return true;
  }
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return true;
  }
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return true;
  }
  const private172 = host.match(/^172\.(\d{1,2})\.\d{1,3}\.\d{1,3}$/);
  if (private172) {
    const octet = Number(private172[1]);
    return octet >= 16 && octet <= 31;
  }
  return false;
}

export function resolveStorefrontApiBaseUrl(options?: {
  apiBaseUrl?: string;
  hostname?: string;
}): string {
  if (options?.apiBaseUrl?.trim()) {
    return options.apiBaseUrl.replace(/\/+$/, '');
  }

  const hostname = options?.hostname
    ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (hostname && !isLocalDevHost(hostname)) {
    return DEFAULT_API_BASE_URL;
  }

  // Edge/njk commerce is a classic IIFE (not an ES module) — import.meta is a syntax error there.
  // Storefront bootstrap sets window.apiBaseUrl; Vite/React callers may pass options.apiBaseUrl.
  if (typeof window !== 'undefined') {
    const bootstrapBase = (window as { apiBaseUrl?: unknown }).apiBaseUrl;
    if (typeof bootstrapBase === 'string' && bootstrapBase.trim()) {
      return bootstrapBase.replace(/\/+$/, '');
    }
  }

  return 'http://localhost:3001'.replace(/\/+$/, '');
}

export function buildStorefrontContactMessage(input: {
  name?: string;
  message: string;
  maxLength?: number;
}): string {
  const customerName = input.name?.trim() ?? '';
  const baseMessage = input.message.trim();
  const fullMessage = `${customerName ? `Name: ${customerName}\n\n` : ''}${baseMessage}`;
  const maxLength = input.maxLength ?? 2000;
  return fullMessage.slice(0, maxLength);
}

export async function submitStorefrontContactTicket(
  input: StorefrontContactTicketInput,
): Promise<StorefrontContactTicketResult> {
  const shopSlug = input.shopSlug.trim();
  if (!shopSlug) {
    throw new Error('Store data is not ready yet. Please try again in a moment.');
  }

  const normalizedEmail = input.email.trim().toLowerCase();
  const subject = input.title?.trim() ?? '';
  const title = subject.length >= 2 ? subject.slice(0, 30) : 'Contact Request';
  const message = buildStorefrontContactMessage({
    name: input.name,
    message: input.message,
  });
  const invoiceId = input.invoiceId?.trim() || undefined;
  const apiBaseUrl = resolveStorefrontApiBaseUrl({ apiBaseUrl: input.apiBaseUrl });

  const response = await fetch(
    `${apiBaseUrl}/v1/storefront/shops/name/${encodeURIComponent(shopSlug)}/tickets`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email: normalizedEmail,
        title,
        message,
        ...(invoiceId ? { invoice_id: invoiceId } : {}),
      }),
    },
  );

  const payload = await response.json().catch(() => null) as {
    message?: string;
    error?: string;
    data?: { uniqid?: string };
  } | null;

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Failed to send your message.');
  }

  const uniqid = payload?.data?.uniqid;
  if (!uniqid) {
    throw new Error('Ticket created, but the response was incomplete.');
  }

  return { uniqid };
}

export type StorefrontSocialLinks = {
  discord?: string | null;
  telegram?: string | null;
};

export function resolveStorefrontSocialLinks(store: {
  discord_link?: string | null;
  telegram_link?: string | null;
  social?: Record<string, string | null | undefined> | null;
} | null | undefined): StorefrontSocialLinks {
  return {
    discord: store?.discord_link ?? store?.social?.discord ?? null,
    telegram: store?.telegram_link ?? store?.social?.telegram ?? null,
  };
}
